// Map SDK models into the app's shared native/web billing models.

import type {
  CustomerInfo as NativeCustomerInfo,
  PurchasesOffering as NativeOffering,
  PurchasesPackage as NativePackage,
} from '@revenuecat/purchases-capacitor';
import type {
  AppCustomerInfo,
  AppEntitlementInfo,
  AppOffering,
  AppPackage,
  AppPackageType,
  AppStore,
} from './types';

// Native's PACKAGE_TYPE and Store enums already use our canonical spelling,
// so these are identity mappings with a safe fallback for values added to
// the SDK after this was written.
const PACKAGE_TYPES = new Set<AppPackageType>([
  'LIFETIME',
  'ANNUAL',
  'SIX_MONTH',
  'THREE_MONTH',
  'TWO_MONTH',
  'MONTHLY',
  'WEEKLY',
  'CUSTOM',
  'UNKNOWN',
]);

const STORES = new Set<AppStore>([
  'PLAY_STORE',
  'APP_STORE',
  'MAC_APP_STORE',
  'STRIPE',
  'RC_BILLING',
  'PADDLE',
  'AMAZON',
  'GALAXY',
  'PROMOTIONAL',
  'EXTERNAL',
  'TEST_STORE',
  'UNKNOWN_STORE',
]);

function toPackageType(value: string): AppPackageType {
  return PACKAGE_TYPES.has(value as AppPackageType) ? (value as AppPackageType) : 'UNKNOWN';
}

function toStore(value: string): AppStore {
  return STORES.has(value as AppStore) ? (value as AppStore) : 'UNKNOWN_STORE';
}

export function fromNativePackage(pkg: NativePackage): AppPackage {
  return {
    identifier: pkg.identifier,
    packageType: toPackageType(pkg.packageType),
    product: {
      identifier: pkg.product.identifier,
      title: pkg.product.title,
      price: pkg.product.price,
      priceString: pkg.product.priceString,
    },
    _sdk: pkg,
  };
}

export function fromNativeOffering(offering: NativeOffering): AppOffering {
  return {
    identifier: offering.identifier,
    availablePackages: offering.availablePackages.map(fromNativePackage),
  };
}

export function fromNativeCustomerInfo(info: NativeCustomerInfo): AppCustomerInfo {
  const active: Record<string, AppEntitlementInfo> = {};
  for (const [id, ent] of Object.entries(info.entitlements.active)) {
    active[id] = {
      identifier: ent.identifier,
      isActive: ent.isActive,
      store: toStore(ent.store),
      productIdentifier: ent.productIdentifier,
    };
  }
  return {
    entitlements: { active },
    activeSubscriptions: info.activeSubscriptions,
    managementURL: info.managementURL ?? null,
  };
}
